/**
 * This component is meant to append to the Esri's sketch panel and serve as a
 * container for any extensions we'd like to add to the panel. This consolidates
 * integration behavior instead of it being reimplmented by each extension.
 */
import { useEffect, useState, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { SKETCH_PANEL_SELECTOR } from './const';
import { SketchPanelExtensionContainer } from './styles';

export interface SketchPanelExtensionProps {
  /**
   * Indicates if the Sketch widget toolbar (that this button is embedded into) is visible.
   * NOTE: The Sketch widget's own visibility tends to lag our setting of it to
   *   be visible. So set this to the same state value used to track Sketch
   *   widget's visibility.
   */
  isSketchVisible: boolean;
  children: ReactNode;
}

export const SketchPanelExtension = ({
  isSketchVisible,
  children,
}: SketchPanelExtensionProps) => {
  const [sketchPanel, setSketchPanel] = useState<Element | null>(null);

  // When the sketch panel is visible, poll for the element until it is found.
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const getPanel = () => {
      const panel = document.querySelector(SKETCH_PANEL_SELECTOR);
      if (panel) {
        setSketchPanel(panel);
      } else {
        timeoutId = setTimeout(getPanel, 100);
      }
    };

    if (isSketchVisible) {
      getPanel();
    } else {
      setSketchPanel(null);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isSketchVisible]);

  // Only render the portal if the panel exists AND is still in the document
  // This prevents React from trying to update a portal target that's been removed from the DOM
  if (!isSketchVisible || !sketchPanel || !document.contains(sketchPanel)) {
    return null;
  }

  return createPortal(
    <SketchPanelExtensionContainer>{children}</SketchPanelExtensionContainer>,
    sketchPanel,
  );
};
